import { useState } from "react";
import { Play, Loader2, Send, X, Code2 } from "lucide-react";
import { runPython } from "@/lib/pyodideLoader";
import { runOnPiston } from "@/lib/pistonRunner";

const LANG_OPTIONS = [
  { value: "python", label: "Python", placeholder: "# Write your Python code here\nprint('Hello, Brimi!')" },
  { value: "java", label: "Java", placeholder: "// Write your Java code here\npublic class Main {\n  public static void main(String[] args) {\n    System.out.println(\"Hello, Brimi!\");\n  }\n}" },
];

/**
 * Multiline code editor for students to type, run and submit code to the AI tutor.
 * Renders inline inside ChatPage. Supports Python (via Pyodide) and Java (via Piston).
 */
export const CodeEditor = ({ defaultLanguage = "python", onSend, onClose, disabled = false }) => {
  const [language, setLanguage] = useState(defaultLanguage);
  const [code, setCode] = useState(
    LANG_OPTIONS.find((l) => l.value === defaultLanguage)?.placeholder || ""
  );
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);

  const changeLang = (next) => {
    setLanguage(next);
    const ph = LANG_OPTIONS.find((l) => l.value === next)?.placeholder || "";
    setCode(ph);
    setOutput(null);
  };

  const onRun = async () => {
    setRunning(true);
    setOutput(null);
    const result =
      language === "python"
        ? await runPython(code)
        : await runOnPiston(code, language);
    setOutput(result);
    setRunning(false);
  };

  const onSubmit = () => {
    if (!onSend) return;
    const lines = [];
    lines.push(`Here's my attempt:\n\n\`\`\`${language}\n${code.trim()}\n\`\`\``);
    if (output) {
      if (output.ok && output.stdout) {
        lines.push(`\nOutput:\n\`\`\`\n${output.stdout.trim()}\n\`\`\``);
      } else if (!output.ok) {
        const err = output.stderr || output.error || "Unknown error";
        lines.push(`\nThis is the error I got:\n\`\`\`\n${err.trim()}\n\`\`\``);
      } else {
        lines.push("\n(Ran with no output.)");
      }
    }
    lines.push("\n\nCan you check my code and explain what's right or wrong?");
    onSend(lines.join(""));
  };

  return (
    <div
      data-testid="inline-code-editor"
      className="border border-border rounded-2xl overflow-hidden bg-background"
    >
      <div className="flex items-center justify-between px-3 py-2 bg-foreground/95 text-background/80 text-xs">
        <div className="flex items-center gap-2">
          <Code2 className="w-4 h-4" />
          <span className="font-bold uppercase tracking-wider">Code playground</span>
          <select
            data-testid="code-editor-lang"
            value={language}
            onChange={(e) => changeLang(e.target.value)}
            className="ml-2 bg-background/15 text-background border border-background/20 rounded px-2 py-0.5 text-xs font-mono"
          >
            {LANG_OPTIONS.map((l) => (
              <option key={l.value} value={l.value} className="text-foreground">
                {l.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="code-editor-close"
          className="p-1 rounded hover:bg-background/10"
          title="Close editor"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <textarea
        data-testid="code-editor-textarea"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        spellCheck={false}
        className="w-full bg-foreground/90 text-background font-mono text-xs sm:text-sm p-3 outline-none resize-y min-h-[160px] max-h-[40vh]"
        placeholder={LANG_OPTIONS.find((l) => l.value === language)?.placeholder}
      />

      <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-t border-border">
        <button
          type="button"
          onClick={onRun}
          disabled={running || !code.trim()}
          data-testid="code-editor-run"
          className="pill-btn bg-[hsl(var(--brimi-mint))] hover:bg-[hsl(var(--brimi-mint))]/90 text-foreground text-sm px-4 py-1.5 disabled:opacity-50"
        >
          {running ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4 fill-current" />
          )}
          {running ? "Running…" : "Run"}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={disabled || !code.trim()}
          data-testid="code-editor-send"
          className="pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white text-sm px-4 py-1.5 disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
          Send to AI tutor
        </button>
      </div>

      {output && (
        <div
          data-testid="code-editor-output"
          className={`px-3 py-2 text-xs sm:text-sm font-mono whitespace-pre-wrap border-t border-border max-h-[35vh] overflow-y-auto ${
            output.ok ? "bg-[hsl(var(--brimi-mint))]/15" : "bg-destructive/10"
          }`}
        >
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
            {output.ok ? "Output" : "Error"}
          </div>
          {output.stdout && <div>{output.stdout}</div>}
          {output.stderr && <div className="text-destructive">{output.stderr}</div>}
          {output.error && !output.ok && (
            <div className="text-destructive mt-1">{output.error}</div>
          )}
          {output.ok && !output.stdout && !output.stderr && (
            <div className="opacity-60 italic">(no output — try adding a print/System.out.println statement)</div>
          )}
        </div>
      )}
    </div>
  );
};
